/* vp-cadence-chart.js -- one shipment per subscriber on the vegan builder.
 *
 * The builder chooses each line's cadence independently, from that variant's own
 * ladder. For a single-size cart that is correct:
 *
 *     all 15-serving   1 / 2 / 3+ bags  ->  21 / 32 / 45 days
 *     all 25-serving   1 / 2 / 3+ bags  ->  20 / 40 / 60 days
 *
 * (The 15-serving ladder was 15/30/45 until 2026-09-22; the two short cadences
 * churned and moved to 21 and 32. Those ladders are enforced by the allow-list
 * in vp-plan-guard.js, not here.)
 *
 * A MIXED cart gets one of each. Adding a 15-serving Pineapple beside a
 * 25-serving Strawberry renders "every 40 & 45 days" and posts two different
 * selling plans, so Shopify opens TWO subscription contracts whose renewal dates
 * drift apart -- the customer is billed, shipped and emailed twice, forever.
 *
 * Loop merges contracts that fall due on the same day (order #402242 merged
 * plans 2030141519 and 2030174287 into one order), so the whole fix is to put
 * every line of a mixed cart on a single cadence. Per the agreed chart, mixed
 * carts follow the 25-serving ladder:
 *
 *     mixed   2 bags -> 40 days       3+ bags -> 60 days
 *
 * A one-bag cart cannot be mixed, so that tier does not exist.
 *
 * WHY THE PLAN IDS ARE RESOLVED AT RUNTIME rather than written down here: the
 * 40- and 60-day plans for 15-serving bags (2097578063 / 2097610831) are
 * deliberately stripped from #vp-data by vp-plan-guard.js, because they must
 * never be offered as a choice on a single-size cart. They are read instead from
 * the product JSON, which is also what makes this correct for Pineapple-15 --
 * that variant carries two plans for the same cadence, in two different groups.
 *
 * PURE CARTS ARE LEFT ALONE. The builder already gets them right, so rewriting
 * them would be risk with no benefit. This file only ever touches a cart that
 * holds both sizes.
 */
(function () {
  "use strict";

  if (!document.getElementById("vp-data")) return;      /* builder pages only */

  var MIXED = { 2: 40, 3: 60 };
  var byVariant = null;              /* variantId -> { size, plans: {days: id} } */

  /* Bag size, written to survive the option restructure. Until the split the Size
     option carried one value ("25 Servings") and the real size hid in the Flavor
     parentheses -- "... (15 Servings)". Breaking 15-serving out into its own Size
     value makes the title "15 Servings / Flavor", with no parentheses at all, so a
     regex REQUIRING them would size every 15-serving bag as 25 and this file would
     then never see a cart as mixed. Matching the words anywhere covers both shapes;
     the SKU (-15 on every 15-serving bag) is the belt-and-braces fallback. */
  function sizeOf(variant) {
    if (/\b15\s*servings?\b/i.test(String(variant.title || ""))) return "15";
    if (/-15$/i.test(String(variant.sku || ""))) return "15";
    return "25";
  }
  function daysOf(name) {
    var m = String(name || "").match(/every\s+(\d+)\s*days?/i);
    return m ? parseInt(m[1], 10) : null;
  }

  /* Product JSON is shaped differently from cart JSON: a variant's
     selling_plan_allocations carry only selling_plan_id, and the NAMES (which is
     where the cadence is written) live once, up on selling_plan_groups. So the
     names are indexed first and the allocations resolved through that. */
  function load() {
    if (byVariant) return Promise.resolve(byVariant);
    var path = location.pathname.split("?")[0].replace(/\/+$/, "");
    if (!/\/products\//.test(path)) { byVariant = {}; return Promise.resolve(byVariant); }
    return fetch(path + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var nameById = {};
        (p.selling_plan_groups || []).forEach(function (g) {
          /* Loop parks retired cadences in a group it names "{inactive
             frequency}", and that group ALSO holds a 60-day plan for the
             15-serving bags. Resolving 60 days to that one would subscribe the
             shopper to a plan that is deliberately not for sale, so the whole
             group is skipped -- as are the "(Aftersell)" plans, which bill at
             full list rather than the subscriber rate. */
          if (/inactive/i.test(g.name || "")) return;
          (g.selling_plans || []).forEach(function (sp) {
            if (/aftersell/i.test(sp.name || "")) return;
            nameById[sp.id] = sp.name;
          });
        });

        var map = {};
        (p.variants || []).forEach(function (v) {
          var plans = {};
          (v.selling_plan_allocations || []).forEach(function (a) {
            var d = daysOf(nameById[a.selling_plan_id]);
            /* first wins: two groups can carry the same cadence, and either
               bills on the same day, which is all that matters here */
            if (d !== null && plans[d] === undefined) plans[d] = a.selling_plan_id;
          });
          map[v.id] = { size: sizeOf(v), plans: plans };
        });
        byVariant = map;
        return byVariant;
      })
      .catch(function () { byVariant = {}; return byVariant; });
  }

  /* Cadence this cart should land on, or null to leave it untouched. */
  function targetFor(items) {
    var seen = {}, bags = 0;
    items.forEach(function (i) {
      if (!i || !i.selling_plan) return;          /* one-time lines and the GWP */
      var v = byVariant[i.id];
      if (!v) return;
      seen[v.size] = 1;
      bags += i.quantity || 1;
    });
    if (!seen["15"] || !seen["25"]) return null;  /* single-size: builder is right */
    return MIXED[bags >= 3 ? 3 : 2] || null;
  }

  function align(items) {
    var t = targetFor(items);
    if (!t) return false;
    var changed = false;
    items.forEach(function (i) {
      if (!i || !i.selling_plan) return;
      var v = byVariant[i.id];
      if (!v) return;
      var want = v.plans[t];
      /* If a variant has no plan at the target cadence, leave the line as the
         builder set it. A split subscription is bad; a failed add is worse. */
      if (want && i.selling_plan !== want) { i.selling_plan = want; changed = true; }
    });
    return changed;
  }

  /* ---- what is actually charged ------------------------------------------
     Rewriting the request is the only place that can guarantee the cart matches
     the chart, whatever the builder decided. */
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      if (/\/cart\/add(\.js)?/.test(url) && init && init.body && byVariant) {
        var body = JSON.parse(init.body);
        if (body && body.items && body.items.length && align(body.items)) {
          return nativeFetch.call(this, input,
            Object.assign({}, init, { body: JSON.stringify(body) }));
        }
      }
    } catch (e) { /* fall through and send it untouched */ }
    return nativeFetch.apply(this, arguments);
  };

  /* ---- what the shopper is shown -----------------------------------------
     The charge and the label have to agree, so the same rule drives the
     "every N days" line. Only overwritten for a mixed cart; otherwise the
     builder's own text already matches. */
  function selection() {
    var seen = {}, bags = 0;
    [].slice.call(document.querySelectorAll(".vp-chip")).forEach(function (c) {
      var scope = c.querySelector(".vp-rowctl") || c;
      var qty = 0;
      [].slice.call(scope.querySelectorAll("*")).forEach(function (e) {
        if (e.children.length === 0 && /^\d+$/.test((e.textContent || "").trim())) {
          qty = parseInt(e.textContent, 10);
        }
      });
      if (!qty) return;
      var pr = (c.querySelector(".pr") || {}).textContent || "";
      seen[/15\s*srv/i.test(pr) ? "15" : "25"] = 1;
      bags += qty;
    });
    return { mixed: !!(seen["15"] && seen["25"]), bags: bags };
  }

  function paint() {
    var el = document.querySelector(".vp-freq");
    if (!el) return;
    var s = selection();
    if (!s.mixed || s.bags < 2) return;
    var t = MIXED[s.bags >= 3 ? 3 : 2];
    if (!t) return;
    var want = "every " + t + " days";
    if ((el.textContent || "").trim() !== want) el.textContent = want;
  }

  load().then(function () {
    paint();
    var timer;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(paint, 60);
    }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  });
})();